import { useRef, useState } from "react";
import { Panel } from "primereact/panel";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ConfirmPopup } from "primereact/confirmpopup";
import { useAuthContext } from "../../../../hooks/useAuthContext";
import { PERMISSION_TRAINING_STUDENT_OBSERVATIONS } from "../../../../permissions";
import studentsAPI from "../../../../services/students";
import AddStudentObservationModal from "./AddStudentObservationModal";
import { useStudentObservationActions } from "./useStudentObservationActions";
import {
  formatObservationDate,
  ObservationApproval,
  ObservationResponseDialog,
  ObservationVisibility,
} from "./StudentObservationCells";

export { formatObservationDate, approverRoleLabel } from "./StudentObservationCells";

const StudentObservationsPanelContent = ({ student, schoolId, trainingId, className, header }) => {
  const toast = useRef(null);
  const addModalRef = useRef(null);
  const { canCreate, hasActions, renderActions } = useStudentObservationActions({ schoolId, trainingId, toast });
  const [responseObservation, setResponseObservation] = useState(null);

  const observationsQuery = studentsAPI.ListObservations(schoolId, trainingId, "", student?.id);
  const observations = (observationsQuery?.data?.data || []).map((observation) =>
    observation?.student_id ? observation : { ...observation, student_id: student?.id }
  );
  const internalCount = observations.filter((observation) => !observation?.notified).length;

  const renderApproval = (observation) => (
    <ObservationApproval observation={observation} onShowResponse={setResponseObservation} />
  );

  const renderVisibility = (observation) => <ObservationVisibility observation={observation} />;

  const renderCards = () =>
    observations.length === 0 ? (
      <div className="py-3 text-center text-500">Aucune observation.</div>
    ) : (
      <div className="flex flex-column gap-2" data-testid="student-observations-cards">
        {observations.map((observation) => (
          <div
            key={observation.id}
            className="border-1 surface-border border-round p-3 flex flex-column gap-2"
            data-testid={`student-observation-card-${observation.id}`}
          >
            <div className="flex justify-content-between align-items-center gap-2 text-sm text-600">
              <span className="white-space-nowrap">{formatObservationDate(observation?.issued_at)}</span>
              <span className="text-right">
                <i className="pi pi-user text-xs mr-1" />
                {observation?.author?.name || "-"}
              </span>
            </div>
            <div className="line-height-3" style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}>
              {observation?.message}
            </div>
            <div className="flex flex-wrap align-items-start justify-content-between gap-2 border-top-1 surface-border pt-2">
              {renderVisibility(observation)}
              {renderApproval(observation)}
            </div>
            {hasActions(observation) && (
              <div className="flex justify-content-end">{renderActions(observation)}</div>
            )}
          </div>
        ))}
      </div>
    );

  const panelHeader = `${header} (${observations.length}${internalCount > 0 ? ` dont ${internalCount} interne${internalCount > 1 ? "s" : ""}` : ""})`;

  return (
    <>
      <Toast ref={toast} position="bottom-center" />
      <ConfirmPopup />
      <Panel
        header={panelHeader}
        toggleable
        className={className}
        icons={
          canCreate ? (
            <Button
              icon="pi pi-plus"
              label="Ajouter"
              size="small"
              text
              className="mr-2"
              onClick={() => addModalRef.current?.show(student)}
            />
          ) : null
        }
      >
        {observationsQuery?.isLoading && <div className="py-4 text-center text-500">Chargement...</div>}
        {observationsQuery?.isError && (
          <div className="py-2 text-center text-red-500">
            {observationsQuery?.error?.message || "Impossible de récupérer les observations."}
          </div>
        )}
        {!observationsQuery?.isLoading && !observationsQuery?.isError && renderCards()}
      </Panel>
      <ObservationResponseDialog observation={responseObservation} onHide={() => setResponseObservation(null)} />
      <AddStudentObservationModal ref={addModalRef} schoolId={schoolId} trainingId={trainingId} />
    </>
  );
};

const StudentObservationsPanel = ({ student, schoolId, trainingId, className, header = "Observations" }) => {
  const { hasPermission } = useAuthContext();
  if (!student?.id || !hasPermission(PERMISSION_TRAINING_STUDENT_OBSERVATIONS.LIST)) {
    return null;
  }
  return (
    <StudentObservationsPanelContent
      student={student}
      schoolId={schoolId}
      trainingId={trainingId}
      className={className}
      header={header}
    />
  );
};

export default StudentObservationsPanel;
